<template>
  <div :class="compact ? 'sp-compact' : 'sp-qv__wrap'">
    <div v-if="!compact" class="sp-qv__head">
      <initials-badge :name="hit.title || '?'" :size="44" :title="entity.singular"/>
      <div style="min-width: 0; flex: 1 1 auto">
        <h3 class="sp-qv__title">{{ hit.title }}</h3>
        <div class="sp-qv__meta">
          {{ entity.singular }}<template v-if="hit.customId"> · {{ hit.customId }}</template>
        </div>
      </div>
    </div>
    <div v-if="!compact && (statusLabel || tags.length)" class="d-flex flex-wrap" style="gap: 6px">
      <span v-if="statusLabel" :class="tone" class="tag tag--sm">{{ statusLabel }}</span>
      <span v-for="tag in tags" :key="tag.text" :class="tag.tone" class="tag tag--sm">{{ tag.text }}</span>
    </div>
    <div v-if="facts.length" class="sp-facts">
      <template v-for="(fact, i) in facts">
        <span :key="`l${i}`" class="sp-facts__label">{{ fact.label }}</span>
        <span :key="`v${i}`" class="sp-facts__value">
          <a v-if="fact.to" :href="hrefOf(fact.to)" @click.prevent="$emit('navigate', fact.to, $event.metaKey || $event.ctrlKey)">{{ fact.value }}</a>
          <template v-else>{{ fact.value }}</template>
          <span v-if="fact.detail" class="sp-facts__detail">{{ fact.detail }}</span>
        </span>
      </template>
    </div>
    <search-timetable v-if="schedule" :compact="compact" :schedule="schedule" @navigate="(to, newTab) => $emit('navigate', to, newTab)"/>
    <div v-if="loading" aria-busy="true" aria-label="Weitere Infos werden geladen" class="sp-skel-qv" role="status">
      <div class="sp-facts">
        <template v-for="n in 3">
          <span :key="`sl${n}`" class="sp-skel" style="width: 70%"/>
          <span :key="`sv${n}`" :style="{ width: `${90 - n * 15}%` }" class="sp-skel"/>
        </template>
      </div>
      <div v-if="hasScheduleSkeleton" class="sp-skel sp-skel--block"/>
      <div v-if="hasLogSkeleton" class="sp-logs">
        <div v-for="n in 3" :key="`lg${n}`" class="sp-log">
          <span class="sp-skel sp-log__icon"/>
          <span class="sp-log__body" style="flex: 1 1 auto; gap: 5px">
            <span class="sp-skel" style="width: 45%"/>
            <span class="sp-skel" style="width: 85%"/>
          </span>
        </div>
      </div>
    </div>
    <div v-else-if="!facts.length && !schedule" class="sp-loading">Keine weiteren Infos verfügbar.</div>
    <div v-if="logs.length" class="sp-logs">
      <template v-for="group in logGroups">
        <div :key="`h${group.key}`" class="sp-logs__title">
          <v-icon x-small>{{ group.icon }}</v-icon>{{ group.label }}
        </div>
        <component :is="log.to ? 'a' : 'div'" v-for="log in group.logs" :key="log.id" :href="log.to ? hrefOf(log.to) : undefined"
                   :title="log.to ? 'Im Profil anzeigen' : undefined" class="sp-log"
                   @click="openLog(log, $event)">
          <span :class="log.tone" class="sp-log__icon"><v-icon x-small>{{ log.icon }}</v-icon></span>
          <span class="sp-log__body">
            <span class="sp-log__meta">{{ metaOf(log) }}</span>
            <span v-if="log.text" class="sp-log__text">{{ log.text }}</span>
          </span>
        </component>
      </template>
    </div>
    <div class="sp-actions">
      <button v-if="canOpen" class="cta cta--primary" type="button" @click="$emit('open', hit)">
        <v-icon class="cta__icon" small>mdi-arrow-right</v-icon>{{ openLabel }}
      </button>
      <button v-if="canOpen" class="cta cta--quiet" type="button" @click="$emit('open-tab', hit)">
        <v-icon class="cta__icon" small>mdi-open-in-new</v-icon>Neuer Tab
      </button>
      <button v-for="link in links" :key="link.label" class="cta cta--quiet" type="button" @click="$emit('navigate', link.to)">
        <v-icon class="cta__icon" small>{{ link.icon }}</v-icon>{{ link.label }}
      </button>
      <a v-if="phone" :href="`tel:${phoneDigits}`" aria-label="Anrufen" class="cta cta--quiet sp-actions__icon" v-tip="'Anrufen'">
        <v-icon small>mdi-phone-outline</v-icon>
      </a>
      <a v-if="mail" :href="`mailto:${mail}`" aria-label="Mail schreiben" class="cta cta--quiet sp-actions__icon" v-tip="'Mail schreiben'">
        <v-icon small>mdi-email-outline</v-icon>
      </a>
      <button v-if="phone" aria-label="Nummer kopieren" class="cta cta--quiet sp-actions__icon" v-tip="'Nummer kopieren'" type="button"
              @click="copyPhone">
        <v-icon small>mdi-content-copy</v-icon>
      </button>
      <button :aria-label="pinned ? 'Nicht mehr anheften' : 'Anheften'" :aria-pressed="String(pinned)"
              :class="{ 'sp-actions__pin--on': pinned }" v-tip="pinned ? 'Nicht mehr anheften' : 'Anheften'"
              class="cta cta--quiet sp-actions__icon sp-actions__pin" type="button" @click="togglePin">
        <v-icon small>{{ pinned ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, {PropType} from 'vue';
import {RawLocation} from 'vue-router';
import InitialsBadge from '@/components/common/modern/initialsBadge.vue';
import SearchTimetable from '@/components/app/search/searchTimetable.vue';
import {QuickSchedule} from '@/components/app/search/searchSchedule';
import {ENTITY_BY_KEY, EntityConfig, PaletteEntity, PaletteHit, routeOfEntity, statusText, statusTone} from '@/components/app/search/searchEntities';
import {idOf, LOGGABLE_ENTITIES, previewContacts, previewFacts, QuickContext, QuickFact, QuickLink, QuickLog, QuickTag} from '@/components/app/search/searchQuickViewData';
import {loadQuickContext} from '@/components/app/search/searchQuickViewLoader';
import {SearchMemoryStore} from '@/components/app/search/searchStore';

const LOAD_DELAY_MS = 250;
/** Areas whose quick view usually shows a week strip (placeholder while loading) */
const SCHEDULE_ENTITIES: PaletteEntity[] = ['students', 'parents', 'teachers', 'employees', 'institutions', 'courses'];

const OPEN_LABELS: Record<string, string> = {
  applicants: 'Bewerbung öffnen',
  institutions: 'Standort öffnen',
  courses: 'Kurs öffnen',
  products: 'Produkt öffnen',
  trainings: 'Schulung öffnen',
};

/** Quick view of the focused hit: preview right away, more context loaded lazily */
export default Vue.extend({
  components: {InitialsBadge, SearchTimetable},
  props: {
    hit: {type: Object as PropType<PaletteHit>, required: true},
    /** Inline below the hit (mobile) */
    compact: {type: Boolean, default: false},
  },
  data: () => ({
    context: null as QuickContext | null,
    loading: false,
    loadTimeout: undefined as number | undefined,
  }),
  computed: {
    entity(): EntityConfig {
      return ENTITY_BY_KEY[this.hit.entity];
    },
    pinned(): boolean {
      return SearchMemoryStore.isPinned(this.hit.entity, this.hit.id);
    },
    statusLabel(): string {
      return statusText(this.hit) || (this.hit.isActive === true ? 'Aktiv' : '');
    },
    tone(): string {
      return statusTone(this.hit);
    },
    canOpen(): boolean {
      return !!this.hit.openTo || !!routeOfEntity(this.hit.entity, this.hit.id);
    },
    openLabel(): string {
      return this.hit.openLabel || OPEN_LABELS[this.hit.entity] || 'Profil öffnen';
    },
    facts(): QuickFact[] {
      return [...(this.context ? this.context.facts.filter((el) => ['Letzter Kontakt', 'Offene To-dos'].includes(el.label)) : []),
        ...previewFacts(this.hit),
        ...(this.context ? this.context.facts.filter((el) => !['Letzter Kontakt', 'Offene To-dos'].includes(el.label)) : [])];
    },
    /** Tags of the loaded context; a tag equal to the status of the hit (e.g. "abgesagt") is shown once */
    tags(): QuickTag[] {
      return this.context ? this.context.tags.filter((tag) => tag.text !== this.statusLabel) : [];
    },
    schedule(): QuickSchedule | null {
      return this.context ? this.context.schedule : null;
    },
    hasScheduleSkeleton(): boolean {
      return SCHEDULE_ENTITIES.includes(this.hit.entity);
    },
    hasLogSkeleton(): boolean {
      return LOGGABLE_ENTITIES.includes(this.hit.entity);
    },
    logs(): QuickLog[] {
      return this.context ? this.context.logs : [];
    },
    logGroups(): Array<{ key: string; label: string; icon: string; logs: QuickLog[] }> {
      const pinned = this.logs.filter((log) => log.pinned);
      const newest = this.logs.filter((log) => !log.pinned);
      return [
        {key: 'pinned', label: 'Angepinnt', icon: 'mdi-pin', logs: pinned},
        {key: 'newest', label: 'Neueste Einträge', icon: 'mdi-history', logs: newest},
      ].filter((group) => group.logs.length > 0);
    },
    links(): QuickLink[] {
      const links: QuickLink[] = [...(this.context ? this.context.links : [])];
      const institutionId = idOf(this.hit.preview?.institution) || idOf(this.hit.preview?.institutionId);
      if (this.hit.entity === 'students' && institutionId) {
        links.push({label: 'Standort öffnen', icon: 'mdi-domain', to: `/institutions/${institutionId}`});
      }
      // Opened page differs from the profile (acquisition list item): the profile is offered additionally
      const profile = this.hit.openTo ? routeOfEntity(this.hit.entity, this.hit.id) : null;
      if (profile) {
        links.push({label: this.hit.entity === 'institutions' ? 'Standort öffnen' : 'Profil öffnen', icon: this.entity.icon, to: profile});
      }
      return links;
    },
    phone(): string {
      return previewContacts(this.hit).phones[0] || (this.context ? this.context.phones[0] : '') || '';
    },
    phoneDigits(): string {
      return this.phone.replace(/[^\d+]/g, '');
    },
    mail(): string {
      return previewContacts(this.hit).mails[0] || (this.context ? this.context.mails[0] : '') || '';
    },
  },
  methods: {
    hrefOf(to: RawLocation): string {
      return this.$router.resolve(to).href;
    },
    metaOf(log: QuickLog): string {
      return [log.typeLabel, log.dateText, log.author].filter((el) => el).join(' · ');
    },
    openLog(log: QuickLog, event: MouseEvent) {
      if (!log.to) {
        return;
      }
      event.preventDefault();
      this.$emit('navigate', log.to, event.metaKey || event.ctrlKey);
    },
    togglePin() {
      SearchMemoryStore.togglePin({
        entity: this.hit.entity,
        id: this.hit.id,
        title: this.hit.title,
        subtitle: this.hit.customId || this.hit.subtitle,
      });
    },
    async copyPhone() {
      try {
        await navigator.clipboard.writeText(this.phone);
        this.$$showSnackbar('Nummer kopiert', 'success');
      } catch (e) {
        this.$$showSnackbar('Nummer konnte nicht kopiert werden', 'error');
      }
    },
    scheduleLoad() {
      clearTimeout(this.loadTimeout);
      this.context = null;
      this.loading = true;
      const key = `${this.hit.entity}:${this.hit.id}`;
      this.loadTimeout = setTimeout(async () => {
        const context = await loadQuickContext(this.hit).catch(() => null);
        if (key === `${this.hit.entity}:${this.hit.id}`) {
          this.context = context;
          this.loading = false;
        }
      }, LOAD_DELAY_MS) as unknown as number;
    },
  },
  watch: {
    hit: {
      immediate: true,
      handler(next: PaletteHit, prev?: PaletteHit) {
        if (!prev || next.id !== prev.id || next.entity !== prev.entity) {
          this.scheduleLoad();
        }
      },
    },
  },
  beforeDestroy() {
    clearTimeout(this.loadTimeout);
  },
});
</script>
